// src/components/HeroMock.jsx
import { useEffect, useMemo, useState } from "react";

const SLIDE_MS = 3500; // auto-advance interval

export default function HeroMock() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  // Keep your original design as the first slide (DashboardSlide)
  const slides = useMemo(
    () => [<DashboardSlide key="dash" />, <KanbanSlide key="kanban" />, <MetricsSlide key="metrics" />],
    []
  );

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setI((p) => (p + 1) % slides.length), SLIDE_MS);
    return () => clearInterval(id);
  }, [paused, slides.length]);

  return (
    <div
      className="h-full w-full bg-white/70 backdrop-blur rounded-2xl border border-slate-200 overflow-hidden relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      role="region"
      aria-label="Product previews"
    >
      {/* Slides */}
      {slides.map((slide, idx) => (
        <div
          key={idx}
          className={`absolute inset-0 transition-opacity duration-700 ease-out ${
            i === idx ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden={i !== idx}
        >
          {slide}
        </div>
      ))}

      {/* Dots */}
      <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-2 z-10">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setI(idx)}
            className={`h-2.5 rounded-full transition-all ${
              i === idx ? "w-6 bg-indigo-600" : "w-2.5 bg-slate-300 hover:bg-slate-400"
            }`}
            aria-label={`Show slide ${idx + 1}`}
          />
        ))}
      </div>

      {/* Local keyframes for bar animation + shimmer used by slides */}
      <style>{`
        @keyframes rise {
          0% { transform: scaleY(0.8); opacity: .7; }
          50% { transform: scaleY(1); opacity: 1; }
          100% { transform: scaleY(0.85); opacity: .8; }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
}

/* ---------------- Shared small bits ---------------- */

function Topbar() {
  return (
    <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 bg-white/60">
      <div className="flex items-center gap-2">
        <span className="h-3 w-3 rounded-full bg-rose-400" />
        <span className="h-3 w-3 rounded-full bg-amber-400" />
        <span className="h-3 w-3 rounded-full bg-emerald-400" />
      </div>
      <div className="h-6 w-40 rounded bg-slate-200 relative overflow-hidden">
        <span className="absolute inset-y-0 left-0 w-1/3 bg-white/60 animate-[shimmer_1.6s_linear_infinite]" />
      </div>
    </div>
  );
}

/* ---------------- Slide 1: YOUR original design (unchanged) ---------------- */

function DashboardSlide() {
  return (
    <div className="h-full w-full">
      {/* Top bar */}
      <Topbar />

      <div className="grid grid-cols-12 gap-4 p-4">
        {/* Sidebar */}
        <div className="col-span-3 hidden md:block">
          <div className="space-y-3">
            <div className="h-8 rounded bg-slate-200 animate-pulse" />
            <div className="h-8 rounded bg-slate-200 animate-pulse" />
            <div className="h-8 rounded bg-slate-200 animate-pulse" />
            <div className="h-8 rounded bg-slate-200 animate-pulse" />
          </div>
        </div>

        {/* Main */}
        <div className="col-span-12 md:col-span-9 space-y-4">
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {["MRR", "Active Users", "NPS", "Time to Value"].map((k) => (
              <div key={k} className="rounded-xl border border-slate-200 p-3 bg-white/80">
                <div className="h-3 w-16 rounded bg-slate-200 animate-pulse" />
                <div className="mt-3 h-6 w-24 rounded bg-indigo-200" />
              </div>
            ))}
          </div>

          {/* Chart + table */}
          <div className="grid md:grid-cols-3 gap-4">
            {/* Tiny bar chart */}
            <div className="md:col-span-2 rounded-xl border border-slate-200 p-4 bg-white/80">
              <div className="flex items-end gap-2 h-40">
                {[28, 48, 35, 70, 55, 90, 65].map((h, i) => (
                  <div
                    key={i}
                    className="w-6 rounded-t bg-indigo-400/80 animate-[rise_2.4s_ease-in-out_infinite]"
                    style={{ height: `${h}%`, animationDelay: `${i * 0.15}s` }}
                  />
                ))}
              </div>
              <div className="mt-3 h-3 w-24 rounded bg-slate-200 animate-pulse" />
            </div>

            {/* Table skeleton */}
            <div className="rounded-xl border border-slate-200 p-4 bg-white/80">
              <div className="h-4 w-28 rounded bg-slate-200 animate-pulse" />
              <div className="mt-3 space-y-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-8 rounded bg-slate-200/70 animate-pulse" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Slide 2: Kanban-style preview ---------------- */

function KanbanSlide() {
  return (
    <div className="h-full w-full">
      <Topbar />
      <div className="grid grid-cols-3 gap-4 p-4">
        {["Backlog", "In Progress", "Review"].map((col) => (
          <div key={col} className="rounded-xl border border-slate-200 bg-white/80 p-3">
            <div className="mb-3 h-4 w-24 rounded bg-slate-200" />
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-slate-200 p-3 bg-white relative overflow-hidden"
                >
                  <div className="h-3 w-40 rounded bg-slate-200" />
                  <div className="mt-2 h-3 w-24 rounded bg-slate-200" />
                  <span className="absolute inset-y-0 left-0 w-1/3 bg-slate-100/70 animate-[shimmer_1.8s_linear_infinite]" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Slide 3: Metrics/Tasks preview ---------------- */

function MetricsSlide() {
  return (
    <div className="h-full w-full">
      <Topbar />
      <div className="grid grid-cols-12 gap-4 p-4">
        <div className="col-span-12 lg:col-span-7">
          <div className="h-full rounded-xl border border-slate-200 bg-slate-900 text-slate-100 p-4 text-sm leading-6 overflow-auto">
            <pre className="whitespace-pre-wrap">
              <code>{`// Example task handler
export async function handler(req) {
  const user = await db.user.find(req.query.id);
  const greet = (name) => \`Hello, \${name}!\`;
  return Response.json({ msg: greet(user.name) });
}

// Simple route
app.get("/health", (_, res) => res.json({ ok: true }));`}</code>
            </pre>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-5 space-y-3">
          {["Build & Deploy", "Run Tests", "Generate Report"].map((t, i) => (
            <div key={t} className="rounded-xl border border-slate-200 p-4 bg-white/80">
              <div className="flex items-center justify-between">
                <span className="h-3 w-28 rounded bg-slate-200" />
                <span className="h-3 w-16 rounded bg-emerald-200" />
              </div>
              <div className="mt-3 h-2 rounded bg-slate-200 relative overflow-hidden">
                <span
                  className="absolute inset-y-0 left-0 bg-indigo-400/80"
                  style={{
                    width: ["35%", "68%", "90%"][i],
                    animation: "rise 2.4s ease-in-out 0s infinite",
                    transformOrigin: "left",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
